import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import {
  CreateUserSchema,
  UpdateUserSchema,
  Role,
  type CreateUserInput,
  type UpdateUserInput,
  type UserResponse,
} from '@app/shared';

export interface UserFormProps {
  mode: 'create' | 'edit';
  defaultValues?: Partial<UserResponse>;
  submitting?: boolean;
  onSubmit: (values: CreateUserInput | UpdateUserInput) => void;
  submitLabel?: string;
}

export function UserForm({
  mode,
  defaultValues,
  submitting,
  onSubmit,
  submitLabel,
}: UserFormProps): JSX.Element {
  if (mode === 'create') return <CreateForm submitting={submitting} onSubmit={onSubmit} submitLabel={submitLabel ?? '建立'} />;
  return (
    <EditForm
      defaultValues={defaultValues}
      submitting={submitting}
      onSubmit={onSubmit}
      submitLabel={submitLabel ?? '儲存'}
    />
  );
}

function CreateForm({
  submitting,
  onSubmit,
  submitLabel,
}: {
  submitting: boolean | undefined;
  onSubmit: (v: CreateUserInput) => void;
  submitLabel: string;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(CreateUserSchema),
    defaultValues: {
      username: '',
      displayName: '',
      email: '',
      password: '',
      role: Role.SALES,
      isActive: true,
    },
  });
  const handle: SubmitHandler<CreateUserInput> = (v) => onSubmit(v);
  return (
    <form onSubmit={handleSubmit(handle)} className="grid gap-4 max-w-xl">
      <Field label="Username *" error={errors.username?.message}>
        <input type="text" {...register('username')} className={inputCls} autoComplete="off" />
      </Field>
      <Field label="姓名 *" error={errors.displayName?.message}>
        <input type="text" {...register('displayName')} className={inputCls} />
      </Field>
      <Field label="Email *" error={errors.email?.message}>
        <input type="email" {...register('email')} className={inputCls} />
      </Field>
      <Field label="初始密碼 *" error={errors.password?.message}>
        <input type="password" {...register('password')} className={inputCls} autoComplete="new-password" />
      </Field>
      <Field label="角色 *" error={errors.role?.message}>
        <select {...register('role')} className={inputCls}>
          <option value={Role.ADMIN}>ADMIN</option>
          <option value={Role.SALES}>SALES</option>
          <option value={Role.VIEWER}>VIEWER</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" {...register('isActive')} />
        <span>啟用</span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="justify-self-start bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? '處理中…' : submitLabel}
      </button>
    </form>
  );
}

function EditForm({
  defaultValues,
  submitting,
  onSubmit,
  submitLabel,
}: {
  defaultValues: Partial<UserResponse> | undefined;
  submitting: boolean | undefined;
  onSubmit: (v: UpdateUserInput) => void;
  submitLabel: string;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateUserInput>({
    resolver: zodResolver(UpdateUserSchema),
    defaultValues: {
      displayName: defaultValues?.displayName ?? '',
      email: defaultValues?.email ?? '',
      password: '',
      role: defaultValues?.role ?? Role.SALES,
      isActive: defaultValues?.isActive ?? true,
    },
  });
  const handle: SubmitHandler<UpdateUserInput> = (v) => {
    // 空字串密碼 = 不變
    if (v.password === '' || v.password === undefined) {
      const { password: _p, ...rest } = v;
      onSubmit(rest);
    } else {
      onSubmit(v);
    }
  };
  return (
    <form onSubmit={handleSubmit(handle)} className="grid gap-4 max-w-xl">
      <div className="text-sm text-slate-500">
        Username：<span className="font-mono">{defaultValues?.username ?? '—'}</span>（不可改）
      </div>
      <Field label="姓名" error={errors.displayName?.message}>
        <input type="text" {...register('displayName')} className={inputCls} />
      </Field>
      <Field label="Email" error={errors.email?.message}>
        <input type="email" {...register('email')} className={inputCls} />
      </Field>
      <Field label="重設密碼（留空 = 不變）" error={errors.password?.message}>
        <input type="password" {...register('password')} className={inputCls} autoComplete="new-password" />
      </Field>
      <Field label="角色" error={errors.role?.message}>
        <select {...register('role')} className={inputCls}>
          <option value={Role.ADMIN}>ADMIN</option>
          <option value={Role.SALES}>SALES</option>
          <option value={Role.VIEWER}>VIEWER</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" {...register('isActive')} />
        <span>啟用</span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="justify-self-start bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? '處理中…' : submitLabel}
      </button>
    </form>
  );
}

const inputCls =
  'w-full border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';

function Field(props: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1">
      <span className="text-sm text-slate-700">{props.label}</span>
      {props.children}
      {props.error ? (
        <span role="alert" className="text-sm text-red-600">
          {props.error}
        </span>
      ) : null}
    </label>
  );
}
